// Pure recipe builders — one per write verb.
//
// Each builder takes typed input + a live Song (for id lookups) and
// returns either a validation Err with a human-readable reason or an
// Ok with an Immer producer (`recipe`), a summary string for the UI,
// and optionally `outputs` (e.g. new block ids the agent will want to
// reference). Recipes are pure functions of the draft — they don't
// look at the outer `song` argument at apply time, so the id lookups
// happen twice: once at validate time for fail-fast errors, again
// inside the producer on the draft (so Immer sees the mutations).

import type { EditRecipe } from '../blocks/editContext';
import { parseMini } from '../blocks/mini/parser';
import { REGISTRY } from '../blocks/registry';
import type { ArgSchema } from '../blocks/registry/types';
import { parseScaleString } from '../blocks/tonal/parse';
import {
  findArrangeEntry,
  findEffect,
  findExpression,
  findStatement,
  removeById,
  renameRefs,
} from '../blocks/walk';
import {
  isConditionalEffect,
  isConditionalKind,
  newBlockId,
} from '../blocks/types';
import { buildCounterMelody } from '../musicTheory/counterMelody';
import { buildHumanizeEffect } from '../musicTheory/humanize';
import { isKnownMode, relativeOf } from '../musicTheory/keys';
import type {
  ConditionalEffect,
  ConditionalKind,
  Effect,
  EffectArg,
  Expression,
  Numeric,
  PatternSlot,
  SimpleEffect,
  SimpleEffectKind,
  Song,
  Statement,
  VoiceBlock,
} from '../blocks/types';

export type BuildOk = {
  ok: true;
  recipe: EditRecipe;
  summary: string;
  outputs?: Record<string, unknown>;
};
export type BuildErr = { ok: false; reason: string };
export type BuildResult = BuildOk | BuildErr;

// ─── Arg coercion ──────────────────────────────────────────────────
//
// The tool schema lets the agent send args as a tagged union:
//   { kind: 'num',  value: 0.75 }
//   { kind: 'str',  value: "C minor" }
//   { kind: 'mini', value: "bd*4" }
//   { kind: 'raw',  value: "1 + 2" }
// This function converts that into the internal EffectArg AST.

export type SimpleArg =
  | { kind: 'num'; value: number }
  | { kind: 'str'; value: string }
  | { kind: 'mini'; value: string }
  | { kind: 'raw'; value: string };

export function coerceArg(a: SimpleArg): EffectArg | { error: string } {
  if (!a || typeof a !== 'object') {
    return { error: 'arg must be an object with a kind field' };
  }
  if (a.kind === 'num') {
    if (typeof a.value !== 'number' || !Number.isFinite(a.value)) {
      return { error: 'num arg requires a finite number value' };
    }
    return { kind: 'numeric', value: { kind: 'num', value: a.value } };
  }
  if (a.kind === 'str') {
    if (typeof a.value !== 'string') {
      return { error: 'str arg requires a string value' };
    }
    return { kind: 'string', value: a.value };
  }
  if (a.kind === 'mini') {
    if (typeof a.value !== 'string') {
      return { error: 'mini arg requires a string value' };
    }
    const parsed = parseMini(a.value);
    if (!parsed.ok) {
      return {
        error: `mini-notation parse error: ${parsed.reason}`,
      };
    }
    return {
      kind: 'pattern',
      value: { kind: 'mini', nodes: parsed.nodes },
    };
  }
  if (a.kind === 'raw') {
    if (typeof a.value !== 'string') {
      return { error: 'raw arg requires a string value' };
    }
    return {
      kind: 'numeric',
      value: { kind: 'raw', source: a.value },
    };
  }
  return { error: `unknown arg kind: ${(a as { kind: string }).kind}` };
}

function coerceArgs(args: SimpleArg[]): EffectArg[] | { error: string } {
  if (!Array.isArray(args)) return { error: 'args must be an array' };
  const out: EffectArg[] = [];
  for (let i = 0; i < args.length; i++) {
    const r = coerceArg(args[i]!);
    if ('error' in r) return { error: `args[${i}]: ${r.error}` };
    out.push(r);
  }
  return out;
}

// ─── Kind-aware validation ─────────────────────────────────────────
//
// Layer schema-driven validation on top of `coerceArgs` so wrong arg
// counts and wrong arg types are rejected at the agent boundary,
// rather than producing malformed code that fails later in Strudel.
// Unknown kinds (not in REGISTRY) fall through to shape-only coercion
// — downstream `isSimpleEffectKind` / `isConditionalKind` checks will
// already have rejected them at the callsite.

export function coerceArgsForKind(
  kind: string,
  args: SimpleArg[],
): EffectArg[] | { error: string } {
  const coerced = coerceArgs(args);
  if ('error' in coerced) return coerced;
  const entry = REGISTRY.get(kind);
  if (!entry) return coerced;
  const err = validateAgainstSchema(kind, entry.args, coerced);
  if (err) return { error: err };
  return coerced;
}

export function coerceArgForSlot(
  kind: string,
  argIndex: number,
  value: SimpleArg,
): EffectArg | { error: string } {
  const coerced = coerceArg(value);
  if ('error' in coerced) return coerced;
  const entry = REGISTRY.get(kind);
  if (!entry) return coerced;
  const slot = entry.args[argIndex];
  if (!slot) return coerced;
  const typeErr = checkArgTypeMatch(slot, coerced);
  if (typeErr) return { error: `arg[${argIndex}] (${slot.name}): ${typeErr}` };
  return coerced;
}

function validateAgainstSchema(
  kind: string,
  schema: readonly ArgSchema[],
  coerced: EffectArg[],
): string | null {
  const requiredCount = schema.filter((s) => s.required).length;
  const totalCount = schema.length;
  if (coerced.length < requiredCount || coerced.length > totalCount) {
    if (requiredCount === totalCount) {
      const plural = totalCount === 1 ? '' : 's';
      return `${kind} expects ${totalCount} arg${plural}, got ${coerced.length}`;
    }
    return `${kind} expects ${requiredCount}–${totalCount} args, got ${coerced.length}`;
  }
  for (let i = 0; i < coerced.length; i++) {
    const slot = schema[i]!;
    const err = checkArgTypeMatch(slot, coerced[i]!);
    if (err) return `${kind} arg[${i}] (${slot.name}): ${err}`;
  }
  return null;
}

function checkArgTypeMatch(slot: ArgSchema, arg: EffectArg): string | null {
  // ArgSchema.type → EffectArg.kind. `modulator` is a Numeric variant
  // — at the EffectArg level it's still `numeric`, so both map there.
  const expected: EffectArg['kind'] | null =
    slot.type === 'numeric' || slot.type === 'modulator'
      ? 'numeric'
      : slot.type === 'pattern'
        ? 'pattern'
        : slot.type === 'string'
          ? 'string'
          : null;
  if (!expected) return null;
  if (arg.kind !== expected) {
    return `expected ${expected}, got ${arg.kind}`;
  }
  if (slot.type === 'string' && slot.enum && arg.kind === 'string') {
    if (!slot.enum.includes(arg.value)) {
      return `expected one of [${slot.enum.join(', ')}], got "${arg.value}"`;
    }
  }
  return null;
}

// Pattern from a string: parse as mini-notation, fall back to raw.
function patternFromString(source: string): PatternSlot {
  const parsed = parseMini(source);
  if (parsed.ok) return { kind: 'mini', nodes: parsed.nodes };
  return { kind: 'raw', source };
}

function numeric(value: number): Numeric {
  return { kind: 'num', value };
}

// ─── add_voice ─────────────────────────────────────────────────────

export type AddVoiceInput = {
  kind: 'voice.s' | 'voice.note';
  pattern: string;
  constName?: string;
  effects?: { kind: string; args?: SimpleArg[] }[];
  /** Insert after this statement id. Omit/null for "append to end". */
  after?: string | null;
};

export function buildAddVoice(
  song: Song,
  input: AddVoiceInput,
): BuildResult {
  if (input.kind !== 'voice.s' && input.kind !== 'voice.note') {
    return { ok: false, reason: `kind must be 'voice.s' or 'voice.note'` };
  }
  if (typeof input.pattern !== 'string' || !input.pattern.trim()) {
    return { ok: false, reason: 'pattern must be a non-empty string' };
  }
  if (input.constName !== undefined) {
    if (!isValidIdentifier(input.constName)) {
      return {
        ok: false,
        reason: `constName "${input.constName}" is not a valid JS identifier`,
      };
    }
    if (hasConstNamed(song, input.constName)) {
      return {
        ok: false,
        reason: `a const named "${input.constName}" already exists`,
      };
    }
  }
  if (input.after !== undefined && input.after !== null) {
    if (!findStatement(song, input.after)) {
      return {
        ok: false,
        reason: `no statement with id ${input.after} (for 'after')`,
      };
    }
  }

  const effectsResult = buildEffectList(input.effects ?? []);
  if ('error' in effectsResult) {
    return { ok: false, reason: effectsResult.error };
  }

  const voiceId = newBlockId();
  const statementId = newBlockId();

  const voice: VoiceBlock = {
    id: voiceId,
    kind: input.kind,
    pattern: patternFromString(input.pattern),
    effects: effectsResult,
  };
  const stmt: Statement = input.constName
    ? {
        id: statementId,
        kind: 'const',
        name: input.constName,
        expr: voice,
      }
    : {
        id: statementId,
        kind: 'expression',
        expr: voice,
      };

  const recipe: EditRecipe = (draft) => {
    const insertAt =
      input.after == null
        ? draft.statements.length
        : draft.statements.findIndex((s) => s.id === input.after) + 1;
    draft.statements.splice(insertAt, 0, stmt);
  };

  const summary = `add ${input.kind}${input.constName ? ` as "${input.constName}"` : ''} with pattern "${input.pattern}"${effectsResult.length ? ` (+${effectsResult.length} effect${effectsResult.length > 1 ? 's' : ''})` : ''}`;

  return {
    ok: true,
    recipe,
    summary,
    outputs: { statementId, voiceId },
  };
}

function buildEffectList(
  list: { kind: string; args?: SimpleArg[] }[],
): Effect[] | { error: string } {
  const out: Effect[] = [];
  for (let i = 0; i < list.length; i++) {
    const spec = list[i]!;
    if (typeof spec.kind !== 'string') {
      return { error: `effects[${i}]: kind must be a string` };
    }
    if (isConditionalKind(spec.kind)) {
      // Nested conditional bodies aren't supported in the add_voice
      // shortcut — use wrap_in_conditional for those. Keeps the tool
      // schema shallow; the agent can chain two calls.
      return {
        error: `effects[${i}]: "${spec.kind}" is a conditional — use wrap_in_conditional instead`,
      };
    }
    const coerced = coerceArgsForKind(spec.kind, spec.args ?? []);
    if ('error' in coerced) {
      return { error: `effects[${i}]: ${coerced.error}` };
    }
    out.push({
      id: newBlockId(),
      kind: spec.kind as SimpleEffectKind,
      args: coerced,
    });
  }
  return out;
}

// ─── remove_block ──────────────────────────────────────────────────

export function buildRemoveBlock(song: Song, id: string): BuildResult {
  if (typeof id !== 'string' || !id) {
    return { ok: false, reason: 'id must be a non-empty string' };
  }
  const stmt = findStatement(song, id);
  const expr = stmt ? null : findExpression(song, id);
  const eff = stmt || expr ? null : findEffect(song, id);
  const entry = stmt || expr || eff ? null : findArrangeEntry(song, id);
  if (!stmt && !expr && !eff && !entry) {
    return { ok: false, reason: `no block with id ${id}` };
  }
  const recipe: EditRecipe = (draft) => {
    removeById(draft, id);
  };
  const label = stmt?.kind ?? expr?.kind ?? (eff ? `effect.${eff.kind}` : 'arrange entry');
  return { ok: true, recipe, summary: `remove ${label} (${id})` };
}

// ─── set_pattern ───────────────────────────────────────────────────

export function buildSetPattern(
  song: Song,
  blockId: string,
  miniSource: string,
): BuildResult {
  const expr = findExpression(song, blockId);
  if (!expr) return { ok: false, reason: `no block with id ${blockId}` };
  if (expr.kind !== 'voice.s' && expr.kind !== 'voice.note') {
    return {
      ok: false,
      reason: `block ${blockId} is ${expr.kind}, not a voice — only voices have patterns`,
    };
  }
  if (typeof miniSource !== 'string' || !miniSource.trim()) {
    return { ok: false, reason: 'pattern must be a non-empty string' };
  }
  const parsed = parseMini(miniSource);
  // We still allow raw strings for grammar we can't parse — Strudel
  // accepts arbitrary mini-notation and the block editor stores raw
  // when parse fails. But we surface the parse error to the model so
  // it can self-correct if the source is genuinely malformed.
  const slot: PatternSlot = parsed.ok
    ? { kind: 'mini', nodes: parsed.nodes }
    : { kind: 'raw', source: miniSource };
  const recipe: EditRecipe = (draft) => {
    const target = findExpression(draft, blockId);
    if (target && (target.kind === 'voice.s' || target.kind === 'voice.note')) {
      target.pattern = slot;
    }
  };
  const warning = parsed.ok ? '' : ` [stored as raw: ${parsed.reason}]`;
  return {
    ok: true,
    recipe,
    summary: `set pattern of ${blockId} to "${miniSource}"${warning}`,
  };
}

// ─── add_effect ────────────────────────────────────────────────────

export type AddEffectInput = {
  blockId: string;
  kind: string;
  args?: SimpleArg[];
  /** Index to insert at. Omit to append. */
  at?: number;
};

export function buildAddEffect(
  song: Song,
  input: AddEffectInput,
): BuildResult {
  const expr = findExpression(song, input.blockId);
  if (!expr) return { ok: false, reason: `no block with id ${input.blockId}` };
  if (!hostsEffects(expr)) {
    return {
      ok: false,
      reason: `block ${input.blockId} (${expr.kind}) cannot hold effects — wrap in a voice or ref first`,
    };
  }
  if (typeof input.kind !== 'string') {
    return { ok: false, reason: 'kind must be a string' };
  }
  if (isConditionalKind(input.kind)) {
    return {
      ok: false,
      reason: `"${input.kind}" is a conditional — use wrap_in_conditional instead`,
    };
  }
  const coerced = coerceArgsForKind(input.kind, input.args ?? []);
  if ('error' in coerced) return { ok: false, reason: coerced.error };
  if (
    input.at !== undefined &&
    (typeof input.at !== 'number' || !Number.isInteger(input.at) || input.at < 0)
  ) {
    return { ok: false, reason: '`at` must be a non-negative integer' };
  }

  const effectId = newBlockId();
  const newEffect: SimpleEffect = {
    id: effectId,
    kind: input.kind as SimpleEffectKind,
    args: coerced,
  };
  const recipe: EditRecipe = (draft) => {
    const target = findExpression(draft, input.blockId);
    if (!target || !hostsEffects(target)) return;
    const list = target.effects ?? [];
    if (!target.effects) target.effects = list;
    if (typeof input.at === 'number' && input.at <= list.length) {
      list.splice(input.at, 0, newEffect);
    } else {
      list.push(newEffect);
    }
  };
  return {
    ok: true,
    recipe,
    summary: `add .${input.kind}(…) to ${input.blockId}`,
    outputs: { effectId },
  };
}

// ─── remove_effect ─────────────────────────────────────────────────

export function buildRemoveEffect(song: Song, effectId: string): BuildResult {
  const eff = findEffect(song, effectId);
  if (!eff) return { ok: false, reason: `no effect with id ${effectId}` };
  const recipe: EditRecipe = (draft) => {
    removeById(draft, effectId);
  };
  return {
    ok: true,
    recipe,
    summary: `remove .${eff.kind}(…)`,
  };
}

// ─── update_effect_arg ─────────────────────────────────────────────

export type UpdateEffectArgInput = {
  effectId: string;
  argIndex: number;
  value: SimpleArg;
};

export function buildUpdateEffectArg(
  song: Song,
  input: UpdateEffectArgInput,
): BuildResult {
  const eff = findEffect(song, input.effectId);
  if (!eff) return { ok: false, reason: `no effect with id ${input.effectId}` };
  const args = isConditionalEffect(eff) ? eff.condArgs : eff.args;
  if (
    typeof input.argIndex !== 'number' ||
    input.argIndex < 0 ||
    input.argIndex >= args.length
  ) {
    return {
      ok: false,
      reason: `argIndex ${input.argIndex} out of range (effect has ${args.length} args)`,
    };
  }
  const coerced = coerceArgForSlot(eff.kind, input.argIndex, input.value);
  if ('error' in coerced) return { ok: false, reason: coerced.error };

  const recipe: EditRecipe = (draft) => {
    const target = findEffect(draft, input.effectId);
    if (!target) return;
    const targetArgs = isConditionalEffect(target) ? target.condArgs : target.args;
    targetArgs[input.argIndex] = coerced;
  };
  return {
    ok: true,
    recipe,
    summary: `update .${eff.kind} arg[${input.argIndex}]`,
  };
}

// ─── wrap_in_conditional ───────────────────────────────────────────

export type WrapInConditionalInput = {
  blockId: string;
  kind: string;
  condArgs?: SimpleArg[];
  /** Body effects applied to the transformed copy. Must be simple effects. */
  body: { kind: string; args?: SimpleArg[] }[];
};

export function buildWrapInConditional(
  song: Song,
  input: WrapInConditionalInput,
): BuildResult {
  const expr = findExpression(song, input.blockId);
  if (!expr) return { ok: false, reason: `no block with id ${input.blockId}` };
  if (!hostsEffects(expr)) {
    return {
      ok: false,
      reason: `block ${input.blockId} (${expr.kind}) cannot hold conditionals — must be a voice or ref`,
    };
  }
  if (!isConditionalKind(input.kind)) {
    return { ok: false, reason: `"${input.kind}" is not a conditional kind` };
  }
  if (!Array.isArray(input.body) || input.body.length === 0) {
    return { ok: false, reason: 'body must be a non-empty array of effects' };
  }
  const condArgs = coerceArgsForKind(input.kind, input.condArgs ?? []);
  if ('error' in condArgs) return { ok: false, reason: condArgs.error };
  const body = buildEffectList(input.body);
  if ('error' in body) return { ok: false, reason: body.error };

  const effectId = newBlockId();
  const condEffect: ConditionalEffect = {
    id: effectId,
    kind: input.kind as ConditionalKind,
    condArgs,
    body,
  };
  const recipe: EditRecipe = (draft) => {
    const target = findExpression(draft, input.blockId);
    if (!target || !hostsEffects(target)) return;
    if (!target.effects) target.effects = [];
    target.effects.push(condEffect);
  };
  return {
    ok: true,
    recipe,
    summary: `wrap ${input.blockId} in .${input.kind}(…, x => x.${body.map((e) => e.kind).join('.')}(…))`,
    outputs: { effectId },
  };
}

// ─── wrap_in_stack / wrap_in_arrange ───────────────────────────────
// Both take a list of EXISTING top-level statement ids and replace them
// with a single new const (if constName given) or expression statement
// containing a stack/arrange of references to the named consts.
//
// Constraint: every referenced statement must be a named `const` —
// otherwise there's nothing to reference. If the user wants to stack
// bare expressions, they should give each one a constName first.

export type WrapInStackInput = {
  statementIds: string[];
  constName?: string;
};

export function buildWrapInStack(
  song: Song,
  input: WrapInStackInput,
): BuildResult {
  const refResult = resolveStatementRefs(song, input.statementIds);
  if (!refResult.ok) return refResult;
  if (input.constName !== undefined) {
    if (!isValidIdentifier(input.constName)) {
      return { ok: false, reason: `constName "${input.constName}" invalid` };
    }
    if (hasConstNamed(song, input.constName)) {
      return { ok: false, reason: `const "${input.constName}" already exists` };
    }
  }

  const stack: Expression = {
    id: newBlockId(),
    kind: 'stack',
    children: refResult.names.map((name) => ({
      id: newBlockId(),
      kind: 'ref',
      name,
    })),
  };
  const newStmt: Statement = input.constName
    ? {
        id: newBlockId(),
        kind: 'const',
        name: input.constName,
        expr: stack,
      }
    : { id: newBlockId(), kind: 'expression', expr: stack };

  const recipe: EditRecipe = (draft) => {
    const insertAt = lastIndexOfIds(draft, input.statementIds) + 1;
    draft.statements.splice(insertAt, 0, newStmt);
  };
  return {
    ok: true,
    recipe,
    summary: `stack(${refResult.names.join(', ')})${input.constName ? ` as "${input.constName}"` : ''}`,
    outputs: { statementId: newStmt.id, stackId: stack.id },
  };
}

export type WrapInArrangeInput = {
  entries: { statementId: string; cycles: number }[];
  constName?: string;
};

export function buildWrapInArrange(
  song: Song,
  input: WrapInArrangeInput,
): BuildResult {
  if (!Array.isArray(input.entries) || input.entries.length === 0) {
    return { ok: false, reason: 'entries must be a non-empty array' };
  }
  const statementIds = input.entries.map((e) => e.statementId);
  const refResult = resolveStatementRefs(song, statementIds);
  if (!refResult.ok) return refResult;
  for (let i = 0; i < input.entries.length; i++) {
    const cycles = input.entries[i]!.cycles;
    if (typeof cycles !== 'number' || !Number.isFinite(cycles) || cycles <= 0) {
      return {
        ok: false,
        reason: `entries[${i}].cycles must be a positive number`,
      };
    }
  }
  if (input.constName !== undefined) {
    if (!isValidIdentifier(input.constName)) {
      return { ok: false, reason: `constName "${input.constName}" invalid` };
    }
    if (hasConstNamed(song, input.constName)) {
      return { ok: false, reason: `const "${input.constName}" already exists` };
    }
  }

  const arrange: Expression = {
    id: newBlockId(),
    kind: 'arrange',
    entries: input.entries.map((e, i) => ({
      id: newBlockId(),
      cycles: numeric(e.cycles),
      section: {
        id: newBlockId(),
        kind: 'ref',
        name: refResult.names[i]!,
      },
    })),
  };
  const newStmt: Statement = input.constName
    ? {
        id: newBlockId(),
        kind: 'const',
        name: input.constName,
        expr: arrange,
      }
    : { id: newBlockId(), kind: 'expression', expr: arrange };

  const recipe: EditRecipe = (draft) => {
    const insertAt = lastIndexOfIds(draft, statementIds) + 1;
    draft.statements.splice(insertAt, 0, newStmt);
  };
  return {
    ok: true,
    recipe,
    summary: `arrange(${input.entries.map((e, i) => `${e.cycles}× ${refResult.names[i]}`).join(', ')})${input.constName ? ` as "${input.constName}"` : ''}`,
    outputs: { statementId: newStmt.id, arrangeId: arrange.id },
  };
}

function resolveStatementRefs(
  song: Song,
  ids: string[],
):
  | { ok: true; names: string[] }
  | { ok: false; reason: string } {
  if (!Array.isArray(ids) || ids.length === 0) {
    return { ok: false, reason: 'statementIds must be a non-empty array' };
  }
  const names: string[] = [];
  for (const id of ids) {
    const s = findStatement(song, id);
    if (!s) return { ok: false, reason: `no statement with id ${id}` };
    if (s.kind !== 'const') {
      return {
        ok: false,
        reason: `statement ${id} is ${s.kind}, not a const — give it a constName first`,
      };
    }
    names.push(s.name);
  }
  return { ok: true, names };
}

function lastIndexOfIds(song: Song, ids: string[]): number {
  let last = -1;
  for (const id of ids) {
    const idx = song.statements.findIndex((s) => s.id === id);
    if (idx > last) last = idx;
  }
  return last;
}

// ─── set_cpm / set_cps ─────────────────────────────────────────────

export function buildSetTempo(
  song: Song,
  kind: 'setcpm' | 'setcps',
  value: number,
): BuildResult {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return { ok: false, reason: 'value must be a positive number' };
  }
  const existing = song.statements.find(
    (s) => s.kind === 'setcpm' || s.kind === 'setcps',
  );
  const stmtId = existing?.id ?? newBlockId();
  const recipe: EditRecipe = (draft) => {
    const idx = draft.statements.findIndex(
      (s) => s.kind === 'setcpm' || s.kind === 'setcps',
    );
    const newStmt: Statement = {
      id: stmtId,
      kind,
      value: numeric(value),
    };
    if (idx >= 0) {
      draft.statements[idx] = newStmt;
    } else {
      draft.statements.unshift(newStmt);
    }
  };
  return {
    ok: true,
    recipe,
    summary: `${kind}(${value})`,
    outputs: { statementId: stmtId },
  };
}

// ─── rename_const ──────────────────────────────────────────────────

export function buildRenameConst(
  song: Song,
  oldName: string,
  newName: string,
): BuildResult {
  if (!isValidIdentifier(oldName)) {
    return { ok: false, reason: `oldName "${oldName}" invalid` };
  }
  if (!isValidIdentifier(newName)) {
    return { ok: false, reason: `newName "${newName}" invalid` };
  }
  if (!hasConstNamed(song, oldName)) {
    return { ok: false, reason: `no const named "${oldName}"` };
  }
  if (hasConstNamed(song, newName)) {
    return { ok: false, reason: `const "${newName}" already exists` };
  }
  const recipe: EditRecipe = (draft) => {
    for (const s of draft.statements) {
      if (s.kind === 'const' && s.name === oldName) {
        s.name = newName;
      }
    }
    renameRefs(draft, oldName, newName);
  };
  return { ok: true, recipe, summary: `rename "${oldName}" → "${newName}"` };
}

// ─── add_comment ───────────────────────────────────────────────────

export function buildAddComment(
  song: Song,
  statementId: string,
  text: string,
  position: 'leading' | 'trailing',
): BuildResult {
  if (typeof text !== 'string' || !text.trim()) {
    return { ok: false, reason: 'text must be a non-empty string' };
  }
  if (position !== 'leading' && position !== 'trailing') {
    return { ok: false, reason: `position must be 'leading' or 'trailing'` };
  }
  const stmt = findStatement(song, statementId);
  if (!stmt) {
    return { ok: false, reason: `no statement with id ${statementId}` };
  }
  if (stmt.kind === 'comment') {
    return {
      ok: false,
      reason: 'cannot attach a comment to a comment block — edit the comment content directly',
    };
  }
  const normalized = text.trim().startsWith('//') ? text.trim() : `// ${text.trim()}`;
  const recipe: EditRecipe = (draft) => {
    const target = findStatement(draft, statementId);
    if (!target) return;
    if (position === 'leading') {
      target.leadingComment = normalized;
    } else {
      target.trailingComment = normalized;
    }
  };
  return {
    ok: true,
    recipe,
    summary: `${position} comment on ${statementId}: "${text.trim()}"`,
  };
}

// ─── transpose_voice ───────────────────────────────────────────────

/** Add (or replace) a `.transpose(N)` effect on a voice/ref. */
export function buildTransposeVoice(
  song: Song,
  blockId: string,
  semitones: number,
): BuildResult {
  if (typeof semitones !== 'number' || !Number.isFinite(semitones)) {
    return { ok: false, reason: 'semitones must be a finite number' };
  }
  const expr = findExpression(song, blockId);
  if (!expr) return { ok: false, reason: `no block with id ${blockId}` };
  if (!hostsEffects(expr)) {
    return {
      ok: false,
      reason: `block ${blockId} (${expr.kind}) cannot hold effects`,
    };
  }
  // Two lookups (against `song` then against `draft`) is the recipe
  // pattern's standard shape: capture the id at validate time so the
  // outputs slot can reference it, then re-find against the draft so
  // the mutation lands on the live tree. The captured id is reused so
  // the re-found effect keeps a stable BlockId across the edit (better
  // for any UI that decorates the source by id).
  const existingId = (expr.effects ?? []).find(
    (e) => !isConditionalEffect(e) && e.kind === 'transpose',
  )?.id;
  const effectId = existingId ?? newBlockId();

  const recipe: EditRecipe = (draft) => {
    const target = findExpression(draft, blockId);
    if (!target || !hostsEffects(target)) return;
    if (!target.effects) target.effects = [];
    const idx = target.effects.findIndex(
      (e) => !isConditionalEffect(e) && e.kind === 'transpose',
    );
    const newEffect: SimpleEffect = {
      id: effectId,
      kind: 'transpose',
      args: [{ kind: 'numeric', value: numeric(semitones) }],
    };
    if (idx >= 0) {
      target.effects[idx] = newEffect;
    } else {
      target.effects.push(newEffect);
    }
  };
  return {
    ok: true,
    recipe,
    summary: `transpose ${blockId} by ${semitones} semitones`,
    outputs: { effectId },
  };
}

// ─── swap_to_relative_minor / swap_to_relative_major ───────────────
//
// Walk every `.scale("…")` effect in the song. If it currently
// matches the source mode (major or minor), rewrite the arg to the
// relative key. Voices already in the target mode are left alone —
// keeps the operation idempotent if called twice.

export function buildSwapRelative(
  song: Song,
  direction: 'minor' | 'major',
): BuildResult {
  const sourceMode = direction === 'minor' ? 'major' : 'minor';
  // Pre-scan to ensure at least one scale arg matches — fail fast so
  // the model knows there's nothing to do.
  let hits = 0;
  forEachScaleEffect(song, (eff) => {
    if (matchesSourceMode(eff, sourceMode)) hits += 1;
  });
  if (hits === 0) {
    return {
      ok: false,
      reason: `no .scale("… ${sourceMode}") effects to swap to relative ${direction}`,
    };
  }

  const recipe: EditRecipe = (draft) => {
    forEachScaleEffect(draft, (eff) => {
      if (!matchesSourceMode(eff, sourceMode)) return;
      const arg = eff.args[0];
      if (!arg || arg.kind !== 'string') return;
      const parsed = parseScaleString(arg.value);
      if (!parsed || !isKnownMode(parsed.mode)) return;
      const rel = relativeOf(parsed.root, parsed.mode);
      if (!rel) return;
      eff.args[0] = { kind: 'string', value: `${rel.root} ${rel.mode}` };
    });
  };
  return {
    ok: true,
    recipe,
    summary: `swap ${hits} scale arg${hits > 1 ? 's' : ''} to relative ${direction}`,
  };
}

/** Visit every `.scale(...)` simple effect anywhere in the song —
 *  including inside conditional bodies, stacks, and arranges. The
 *  callback runs against the same `Effect` reference held by the
 *  song (or draft) so mutations stick. */
function forEachScaleEffect(
  song: Song,
  visit: (eff: SimpleEffect) => void,
): void {
  for (const stmt of song.statements) {
    if (stmt.kind !== 'const' && stmt.kind !== 'expression') continue;
    walkExprForScale(stmt.expr, visit);
  }
}

function walkExprForScale(
  expr: Expression,
  visit: (eff: SimpleEffect) => void,
): void {
  if (expr.kind === 'voice.s' || expr.kind === 'voice.note') {
    walkEffectsForScale(expr.effects, visit);
  } else if (expr.kind === 'ref' && expr.effects) {
    walkEffectsForScale(expr.effects, visit);
  } else if (expr.kind === 'stack') {
    for (const c of expr.children) walkExprForScale(c, visit);
  } else if (expr.kind === 'arrange') {
    for (const e of expr.entries) walkExprForScale(e.section, visit);
  }
}

function walkEffectsForScale(
  effects: Effect[],
  visit: (eff: SimpleEffect) => void,
): void {
  for (const eff of effects) {
    if (isConditionalEffect(eff)) {
      walkEffectsForScale(eff.body, visit);
      continue;
    }
    if (eff.kind === 'scale') visit(eff);
  }
}

function matchesSourceMode(
  eff: SimpleEffect,
  sourceMode: 'major' | 'minor',
): boolean {
  const arg = eff.args[0];
  if (!arg || arg.kind !== 'string') return false;
  const parsed = parseScaleString(arg.value);
  return parsed?.mode === sourceMode;
}

// ─── humanize_voice ────────────────────────────────────────────────

export function buildHumanizeVoice(
  song: Song,
  blockId: string,
  jitter?: number,
): BuildResult {
  const expr = findExpression(song, blockId);
  if (!expr) return { ok: false, reason: `no block with id ${blockId}` };
  if (!hostsEffects(expr)) {
    return {
      ok: false,
      reason: `block ${blockId} (${expr.kind}) cannot hold effects`,
    };
  }
  const effect = buildHumanizeEffect(jitter !== undefined ? { jitter } : {});
  // Idempotent: if a humanize-shaped .late already exists, replace it
  // rather than stack a second one. "humanize-shaped" = .late(rand.range(-x, x))
  // with a single arg. A .late(0.125) put there by hand for groove is
  // intentionally left alone.
  const existingId = findExistingHumanize(expr.effects ?? [])?.id;
  const recipe: EditRecipe = (draft) => {
    const target = findExpression(draft, blockId);
    if (!target || !hostsEffects(target)) return;
    if (!target.effects) target.effects = [];
    if (existingId) {
      const idx = target.effects.findIndex((e) => e.id === existingId);
      if (idx >= 0) {
        target.effects[idx] = effect;
        return;
      }
    }
    target.effects.push(effect);
  };
  return {
    ok: true,
    recipe,
    summary: `${existingId ? 'update' : 'add'} humanize on ${blockId} (jitter ${jitter ?? 0.005})`,
    outputs: { effectId: effect.id, replaced: existingId ?? null },
  };
}

function findExistingHumanize(effects: Effect[]): SimpleEffect | null {
  for (const eff of effects) {
    if (isConditionalEffect(eff)) continue;
    if (eff.kind !== 'late') continue;
    if (eff.args.length !== 1) continue;
    const arg = eff.args[0];
    if (!arg || arg.kind !== 'numeric') continue;
    const v = arg.value;
    if (v.kind !== 'modulator') continue;
    if (v.source !== 'rand') continue;
    if (!v.range) continue;
    // Symmetric ±x range is the humanize signature.
    const lo = v.range[0];
    const hi = v.range[1];
    if (lo.kind !== 'num' || hi.kind !== 'num') continue;
    if (lo.value === -hi.value) return eff;
  }
  return null;
}

// ─── propose_counter_melody ────────────────────────────────────────
//
// Reads the source voice's MiniPattern, builds a transposed copy as
// a new note() voice, and inserts it after the source statement.
// Caller picks the interval; the agent tool layer chooses based on
// inferred key.

export type ProposeCounterMelodyInput = {
  sourceBlockId: string;
  intervalSemitones?: number;
  /** Const name for the new voice. Required so it can be stacked later. */
  constName: string;
};

export function buildProposeCounterMelody(
  song: Song,
  input: ProposeCounterMelodyInput,
): BuildResult {
  const expr = findExpression(song, input.sourceBlockId);
  if (!expr) {
    return { ok: false, reason: `no block with id ${input.sourceBlockId}` };
  }
  if (expr.kind !== 'voice.note') {
    return {
      ok: false,
      reason: `source block must be voice.note (got ${expr.kind}) — counter melodies only make sense on pitch patterns`,
    };
  }
  if (expr.pattern.kind !== 'mini') {
    return {
      ok: false,
      reason: 'source pattern is raw — only structured mini patterns can be analysed',
    };
  }
  if (!isValidIdentifier(input.constName)) {
    return { ok: false, reason: `constName "${input.constName}" invalid` };
  }
  if (hasConstNamed(song, input.constName)) {
    return { ok: false, reason: `const "${input.constName}" already exists` };
  }
  // Find the statement holding the source so the new voice slots in
  // right after — keeps related material visually adjacent.
  const sourceStmtId = findContainingStatementId(song, input.sourceBlockId);
  if (!sourceStmtId) {
    return {
      ok: false,
      reason: `source block ${input.sourceBlockId} is nested inside a stack/arrange — extract to a top-level const first`,
    };
  }
  const interval = input.intervalSemitones ?? 4;
  const { pattern: counter, skipped } = buildCounterMelody(expr.pattern.nodes, {
    intervalSemitones: interval,
  });
  const voiceId = newBlockId();
  const newStmt: Statement = {
    id: newBlockId(),
    kind: 'const',
    name: input.constName,
    expr: {
      id: voiceId,
      kind: 'voice.note',
      pattern: { kind: 'mini', nodes: counter },
      effects: [],
    },
  };
  const recipe: EditRecipe = (draft) => {
    // sourceStmtId was validated above; +1 always lands at a valid splice
    // position (== statements.length when source is the last statement).
    const idx = draft.statements.findIndex((s) => s.id === sourceStmtId);
    draft.statements.splice(idx + 1, 0, newStmt);
  };
  const warning =
    skipped.length > 0
      ? ` [warning: ${skipped.length} non-pitch atom${skipped.length > 1 ? 's' : ''} left unshifted: ${skipped.slice(0, 3).join(', ')}${skipped.length > 3 ? '…' : ''}]`
      : '';
  return {
    ok: true,
    recipe,
    summary: `add counter melody "${input.constName}" (+${interval} semitones)${warning}`,
    outputs: { statementId: newStmt.id, voiceId, skipped },
  };
}

function findContainingStatementId(
  song: Song,
  blockId: string,
): string | null {
  for (const stmt of song.statements) {
    if (stmt.kind !== 'const' && stmt.kind !== 'expression') continue;
    if (stmt.expr.id === blockId) return stmt.id;
  }
  return null;
}

// ─── apply_voice_leading ───────────────────────────────────────────
//
// Strudel's `.voicing()` automatically picks chord inversions that
// minimise pitch movement between successive chords. This recipe
// just appends `.voicing()` after `.chord(...)` if it isn't already
// present — minimal but useful, and the engine does the heavy lift.

export function buildApplyVoiceLeading(
  song: Song,
  blockId: string,
): BuildResult {
  const expr = findExpression(song, blockId);
  if (!expr) return { ok: false, reason: `no block with id ${blockId}` };
  if (!hostsEffects(expr)) {
    return {
      ok: false,
      reason: `block ${blockId} (${expr.kind}) cannot hold effects`,
    };
  }
  const effects = expr.effects ?? [];
  const hasChord = effects.some(
    (e) => !isConditionalEffect(e) && e.kind === 'chord',
  );
  if (!hasChord) {
    return {
      ok: false,
      reason: `block ${blockId} has no .chord(...) effect — voice-leading only applies to chord voices`,
    };
  }
  const hasVoicing = effects.some(
    (e) => !isConditionalEffect(e) && e.kind === 'voicing',
  );
  if (hasVoicing) {
    return { ok: false, reason: 'voicing already applied' };
  }
  const effectId = newBlockId();
  const recipe: EditRecipe = (draft) => {
    const target = findExpression(draft, blockId);
    if (!target || !hostsEffects(target)) return;
    if (!target.effects) target.effects = [];
    const chordIdx = target.effects.findIndex(
      (e) => !isConditionalEffect(e) && e.kind === 'chord',
    );
    const voicing: SimpleEffect = {
      id: effectId,
      kind: 'voicing',
      args: [],
    };
    if (chordIdx >= 0) {
      target.effects.splice(chordIdx + 1, 0, voicing);
    } else {
      target.effects.push(voicing);
    }
  };
  return {
    ok: true,
    recipe,
    summary: `apply .voicing() to ${blockId}`,
    outputs: { effectId },
  };
}

// ─── shared helpers ────────────────────────────────────────────────

function hostsEffects(
  e: Expression,
): e is VoiceBlock | (Expression & { kind: 'ref' }) {
  return e.kind === 'voice.s' || e.kind === 'voice.note' || e.kind === 'ref';
}

function isValidIdentifier(name: string): boolean {
  return typeof name === 'string' && /^[A-Za-z_$][A-Za-z0-9_$]*$/.test(name);
}

function hasConstNamed(song: Song, name: string): boolean {
  return song.statements.some((s) => s.kind === 'const' && s.name === name);
}
